import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchJSON } from '../lib/api';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function CertificateVerify() {
  const { code } = useParams();
  const [state, setState] = useState('loading');
  const [info, setInfo] = useState(null);

  useEffect(() => {
    fetchJSON(`/api/verify/${code}`)
      .then((data) => {
        if (data.valid) {
          setInfo(data);
          setState('valid');
        } else {
          setState('invalid');
        }
      })
      .catch(() => setState('invalid'));
  }, [code]);

  return (
    <div className="flex-1 flex items-center justify-center px-4">
      {state === 'loading' && (
        <p className="text-slate2 text-sm">Checking certificate&hellip;</p>
      )}

      {state === 'invalid' && (
        <div className="max-w-md w-full text-center">
          <div className="text-4xl mb-4">&#10060;</div>
          <h1 className="font-display text-2xl text-ink mb-3">Certificate Not Found</h1>
          <p className="text-slate2 text-sm leading-relaxed">
            The verification code{' '}
            <span className="font-mono text-xs bg-cream px-1.5 py-0.5 rounded">{code}</span>{' '}
            does not match any certificate issued by Learning Outcomes Academy. Please check the code and try again.
          </p>
          <Link to="/" className="btn-link mt-8 inline-flex">
            Return to Home
          </Link>
        </div>
      )}

      {state === 'valid' && info && (
        <div className="max-w-lg w-full">
          <div className="bg-white border border-brand-deep/20 rounded-2xl px-8 py-10 text-center shadow-sm">
            <div className="flex items-center justify-center gap-2 text-brand-moss mb-6">
              <span className="text-2xl">&#10003;</span>
              <span className="font-display text-lg text-brand-moss">Valid Certificate</span>
            </div>

            <div className="border-t border-b border-ink/10 py-6 space-y-4">
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-brand-aubergine mb-1">
                  Issued To
                </div>
                <div className="font-display text-xl text-ink">{info.name}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-brand-aubergine mb-1">
                  Course
                </div>
                <div className="font-display text-base text-ink">{info.course}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-brand-aubergine mb-1">
                  Completion Date
                </div>
                <div className="text-sm text-slate2">{formatDate(info.issuedOn)}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-brand-aubergine mb-1">
                  Issued By
                </div>
                <div className="text-sm text-slate2">Learning Outcomes Academy</div>
              </div>
            </div>

            <div className="mt-6">
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate2 mb-1">
                Verification Code
              </div>
              <div className="font-mono text-xs bg-cream border border-ink/10 rounded px-3 py-1.5 inline-block text-ink/70">
                {info.verifyCode}
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-slate2 mt-6">
            This certificate was issued by{' '}
            <a
              href="https://learningoutcomes.academy"
              className="underline hover:text-brand-deep transition-colors"
            >
              learningoutcomes.academy
            </a>
          </p>
        </div>
      )}
    </div>
  );
}
